
import React from 'react';
import { Download, AlertCircle, CheckCircle, Package } from 'lucide-react';

interface ResultViewerProps {
  success?: boolean;
  error?: string;
  imageUrl?: string;
  images?: { image: string; filename: string }[];
  filename?: string;
  optimizedInstruction?: string;
}

export const ResultViewer: React.FC<ResultViewerProps> = ({ 
  success, 
  error, 
  imageUrl, 
  images,
  filename = 'result.png',
  optimizedInstruction
}) => {
  if (!success && !error) return null;

  const handleDownload = (dataUrl: string, name: string) => {
    const link = document.createElement('a');
    link.href = dataUrl;
    link.download = name;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleDownloadAll = () => {
    if (!images) return;
    // Sequential download with delay to prevent browser blocking
    images.forEach((img, index) => {
        setTimeout(() => {
            handleDownload(`data:image/png;base64,${img.image}`, img.filename);
        }, index * 500);
    });
  };

  if (error) {
    return (
      <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 flex items-start gap-3">
        <AlertCircle className="shrink-0 mt-0.5" size={20} />
        <div>
          <h4 className="font-semibold">Error Processing Image</h4>
          <p className="text-sm mt-1 opacity-90">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="mt-8 animate-fade-in">
      <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-lg text-emerald-800 flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
            <CheckCircle size={20} />
            <span className="font-medium">Process completed successfully!</span>
        </div>
        {images && images.length > 1 && (
            <button 
                onClick={handleDownloadAll}
                className="px-4 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm font-medium flex items-center gap-2 transition-colors shadow-sm"
            >
                <Package size={16} /> Download All ({images.length})
            </button>
        )}
      </div>

      {/* Single Image Result */}
      {imageUrl && (
        <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
          <div className="aspect-square relative rounded-lg overflow-hidden bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] bg-slate-100">
            <img 
              src={`data:image/png;base64,${imageUrl}`} 
              alt="Result" 
              className="w-full h-full object-contain"
            />
          </div>
          {optimizedInstruction && (
            <div className="mt-4 p-3 bg-slate-50 rounded-lg text-sm border border-slate-200">
              <span className="font-semibold text-slate-700 block mb-1">Optimized Prompt Used:</span>
              <p className="text-slate-600 italic">{optimizedInstruction}</p>
            </div>
          )}
          <button 
            onClick={() => handleDownload(`data:image/png;base64,${imageUrl}`, filename)}
            className="w-full mt-4 py-2.5 bg-primary-600 hover:bg-primary-700 text-white rounded-lg font-medium flex items-center justify-center gap-2 transition-colors"
          >
            <Download size={18} /> Download Image
          </button>
        </div>
      )}

      {/* Multiple Mockup Results */}
      {images && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {images.map((img, idx) => (
            <div key={idx} className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
               <div className="relative rounded-lg overflow-hidden bg-slate-100 mb-3 aspect-square">
                <img 
                  src={`data:image/png;base64,${img.image}`} 
                  alt={img.filename} 
                  className="w-full h-full object-contain"
                />
              </div>
              <p className="text-sm font-medium text-center mb-3 text-slate-700 truncate" title={img.filename}>{img.filename}</p>
              <button 
                onClick={() => handleDownload(`data:image/png;base64,${img.image}`, img.filename)}
                className="w-full py-2 bg-white border border-slate-300 hover:bg-slate-50 text-slate-700 rounded-lg text-sm font-medium flex items-center justify-center gap-2 transition-colors"
              >
                <Download size={16} /> Download
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
